/**
 * BulkActionToolbar — sticky bar that appears when ≥1 row is selected.
 *
 * Per the plan: "12 selected   Move   Delete   Cancel". Sits at the top
 * of the file list when selection is active, slides out when cleared.
 *
 * Action buttons are bare callbacks; the page wires them to the bulk
 * endpoints (POST /api/files/bulk-delete, /api/files/bulk-move).
 */

import { Box, Button, Stack, Typography } from '@mui/material';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import DriveFileMoveRoundedIcon from '@mui/icons-material/DriveFileMoveRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';

export interface BulkActionToolbarProps {
  count: number;
  busy?: boolean;
  onMove: () => void;
  onDelete: () => void;
  onCancel: () => void;
}

export function BulkActionToolbar({ count, busy, onMove, onDelete, onCancel }: BulkActionToolbarProps) {
  if (count === 0) return null;

  return (
    <Box
      sx={{
        position: 'sticky',
        top: 0,
        zIndex: 10,
        mb: 2,
        p: 1.5,
        borderRadius: 2,
        background: 'rgba(143,245,255,0.10)',
        border: '1px solid rgba(143,245,255,0.30)',
        backdropFilter: 'blur(16px)',
      }}
    >
      <Stack direction="row" alignItems="center" spacing={2}>
        <Typography sx={{ fontWeight: 700, color: '#8ff5ff', minWidth: 100 }}>
          {count} selected
        </Typography>
        <Box sx={{ flex: 1 }} />
        <Button
          size="small"
          startIcon={<DriveFileMoveRoundedIcon />}
          onClick={onMove}
          disabled={busy}
        >
          Move
        </Button>
        <Button
          size="small"
          startIcon={<DeleteOutlineRoundedIcon />}
          onClick={onDelete}
          disabled={busy}
          color="error"
        >
          Delete
        </Button>
        <Button
          size="small"
          startIcon={<CloseRoundedIcon />}
          onClick={onCancel}
          disabled={busy}
        >
          Cancel
        </Button>
      </Stack>
    </Box>
  );
}
